import { Box } from "@mui/material";
import type { ReactNode } from "react";
import { UtilityMessage } from "../../../shared/components/UtilityStates";
import { ConsumptionFilterBar } from "../../../shared/consumption/components/ConsumptionFilterBar";
import {
	ConsumptionError,
	ConsumptionSkeletons,
} from "../../../shared/consumption/components/ConsumptionStates";
import { DataThroughCaption } from "../../../shared/consumption/components/DataThroughCaption";
import { useGasConsumption } from "../../../shared/consumption/hooks/useGasConsumption";
import type { GasZoneSelection } from "../../../shared/consumption/hooks/useGasConsumptionZones";
import type {
	UtilityConsumptionMode,
	UtilityConsumptionPeriod,
} from "../../../shared/consumption/models/UtilityConsumption";
import {
	GAS_ZONE_TREE_LABELS,
	getGasZoneLabel,
	toZoneMeters,
} from "../../../shared/consumption/utils/gasConsumption";
import { getUtilityConsumptionPeriodLabel } from "../../../shared/consumption/utils/calendar";
import { GasAtAGlance } from "./GasAtAGlance";
import { GasConsumptionChart } from "./GasConsumptionChart";

interface GasDashboardProps {
	targetId: number | null;
	zoneSelection: GasZoneSelection;
	areaSelector?: ReactNode;
	mode: UtilityConsumptionMode;
	period: UtilityConsumptionPeriod;
	controls: ReactNode;
}

export const GasDashboard = ({
	targetId,
	zoneSelection,
	areaSelector,
	mode,
	period,
	controls,
}: GasDashboardProps) => {
	const {
		zones,
		zoneError,
		isZoneError,
		isZoneLoading,
		refetchZones,
		treeSelectedKeys,
		requestZoneKeys,
		isEmptySelection,
		changeZoneKeys,
	} = zoneSelection;

	const { data, error, isError, isLoading, refetch } = useGasConsumption({
		targetId,
		enabled: zones !== undefined && zones.length > 0 && !isEmptySelection,
		zoneKeys: requestZoneKeys,
		mode,
		period,
	});

	const zoneByKey = new Map((zones ?? []).map((zone) => [zone.ZoneKey, zone]));

	return (
		<>
			<ConsumptionFilterBar
				meters={toZoneMeters(zones ?? [])}
				isMeterLoading={isZoneLoading}
				selectedMeterIds={treeSelectedKeys}
				meterLabels={GAS_ZONE_TREE_LABELS}
				getMeterItemLabel={(meter) => {
					const zone = zoneByKey.get(meter.ExternalMeterId);
					return zone === undefined
						? meter.ExternalMeterId
						: getGasZoneLabel(zone);
				}}
				areaSelector={areaSelector}
				onMeterChange={changeZoneKeys}
			/>
			{isZoneError ? (
				<ConsumptionError
					subject="Furnace gas zones"
					message={zoneError?.message ?? "Unexpected response."}
					onRetry={() => refetchZones()}
				/>
			) : zones !== undefined && zones.length === 0 ? (
				<UtilityMessage
					title="No furnace gas"
					detail="This area has no furnace, so no gas is measured for it."
				/>
			) : isEmptySelection ? (
				<UtilityMessage
					title="No zones selected"
					detail="Tick at least one zone in the filter to see furnace gas."
				/>
			) : (
				<Box
					sx={{
						display: "flex",
						flex: 1,
						flexDirection: "column",
						gap: 1,
						minHeight: 0,
					}}
				>
					{isError && (
						<ConsumptionError
							subject="Furnace gas"
							message={
								error instanceof Error ? error.message : "Unexpected response."
							}
							onRetry={() => refetch()}
						/>
					)}
					{isLoading && data === undefined && <ConsumptionSkeletons />}
					{data && !data.summary.IsAvailable && (
						<UtilityMessage
							title="No furnace gas recorded"
							detail="The historian has no gas readings for these zones yet."
						/>
					)}
					{data?.summary.IsAvailable && (
						<>
							<DataThroughCaption
								dataThroughUtc={data.summary.DataThroughUtc}
								label="Furnace gas through"
							/>
							<GasAtAGlance
								summary={data.summary}
								millRolling={data.millRolling}
								buckets={data.miniBuckets}
								periodLabel={getUtilityConsumptionPeriodLabel(
									mode,
									period.startUtc,
								)}
							/>
							<GasConsumptionChart
								buckets={data.mainBuckets}
								zones={data.chartZones}
								summary={data.summary}
								mode={mode}
								period={period}
								isDefaultSelection={requestZoneKeys === null}
								controls={controls}
								onZoneFilter={zoneSelection.filterByZone}
							/>
						</>
					)}
				</Box>
			)}
		</>
	);
};
